import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";

const THREAT_TYPES = [
    { id: "phishing", label: "Phishing", icon: "bg-orange-400" },
    { id: "malware", label: "Malware / Virus", icon: "bg-red-500" },
    { id: "data_breach", label: "Fuite de données", icon: "bg-purple-500" },
    { id: "unauthorized_access", label: "Accès non autorisé", icon: "bg-yellow-500" },
    { id: "suspicious_activity", label: "Activité suspecte", icon: "bg-amber-400" },
    { id: "other", label: "Autre", icon: "bg-gray-400" },
];

export function SecurityAlertForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("critical");
    const [threatType, setThreatType] = useState("phishing");
    const [affectedSystems, setAffectedSystems] = useState("");
    const [discoveryTime, setDiscoveryTime] = useState("");
    const [isOngoing, setIsOngoing] = useState(true);
    const [evidence, setEvidence] = useState("");
    const [immediateAction, setImmediateAction] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                threat_type: threatType,
                affected_systems: affectedSystems.split(",").map((s) => s.trim()).filter(Boolean),
                discovery_datetime: discoveryTime || new Date().toISOString(),
                is_ongoing: isOngoing,
                evidence_description: evidence,
                immediate_action_taken: immediateAction,
            },
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                    <strong>Alerte sécurité :</strong> Ce type de ticket est traité en priorité critique.
                    Ne cliquez pas sur les liens suspects et ne téléchargez aucun fichier.
                </p>
            </div>

            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Email de phishing reçu"
                descriptionPlaceholder="Décrivez l'incident de sécurité..."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Type de menace</Label>
                        <Select value={threatType} onValueChange={setThreatType}>
                            <SelectTrigger data-testid="threat-type">
                                <SelectValue>
                                    {(() => { const t = THREAT_TYPES.find((t) => t.id === threatType); return t ? <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${t.icon}`} />{t.label}</span> : null; })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {THREAT_TYPES.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        <span className="flex items-center gap-2.5">
                                            <span className={`w-2.5 h-2.5 rounded-full ${t.icon}`} />
                                            {t.label}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Date/heure de découverte</Label>
                        <Input
                            type="datetime-local"
                            value={discoveryTime}
                            onChange={(e) => setDiscoveryTime(e.target.value)}
                            data-testid="discovery-time"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Systèmes affectés</Label>
                    <Input
                        value={affectedSystems}
                        onChange={(e) => setAffectedSystems(e.target.value)}
                        placeholder="Séparez par des virgules: Messagerie, PC-USER-01..."
                        data-testid="affected-systems"
                    />
                </div>

                <div className="form-group">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="ongoing"
                            checked={isOngoing}
                            onCheckedChange={setIsOngoing}
                            data-testid="is-ongoing"
                        />
                        <Label htmlFor="ongoing" className="text-sm font-medium cursor-pointer">
                            La menace est toujours en cours
                        </Label>
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Description des preuves</Label>
                    <Textarea
                        value={evidence}
                        onChange={(e) => setEvidence(e.target.value)}
                        placeholder="Décrivez ce que vous avez observé (URL suspecte, expéditeur, comportement anormal...)"
                        data-testid="evidence"
                    />
                </div>

                <div className="form-group">
                    <Label className="form-label">Actions immédiates prises</Label>
                    <Textarea
                        value={immediateAction}
                        onChange={(e) => setImmediateAction(e.target.value)}
                        placeholder="Qu'avez-vous fait immédiatement ? (Ex: déconnexion réseau, signalement...)"
                        data-testid="immediate-action"
                    />
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre l'alerte"}
                </Button>
            </div>
        </form>
    );
}
